/* istanbul ignore file */
/* tijdelijk tot feature af is */
import { hasValue } from "adviesbox-shared";
import reactFastCompare from "react-fast-compare";
import { Verkoopstatus } from "../../.generated/forms/formstypes";
import { createISWSideEffect, initISWSideEffect } from "../../shared/components/isw-side-effects/create-isw-helpers";
import { GetAdresEncoded, GetMapsImageUrl } from "../../shared/components/mapsimage/MapImage";
import { SoortFoto, WoningState, VerkoopWoningType, HdnAnders } from "./woning-schema";

const teVerkopenWoningenDraftSideEffects = createISWSideEffect<VerkoopWoningType>(({ has, draft }): void => {
  if (has.verkoopstatus.changed) {
    if (draft.verkoopstatus === "BlijftInBezit") {
      draft.verkoopstatusDatum = null;
      draft.ontbindendeVoorwaardenDatum = null;
      draft.verkoopprijs = null;
      draft.verkoopkosten = null;
      draft.verkoopondervoorwaarden = null;
      draft.constructie = null;
      draft.constructieOmschrijving = null;
    }
  }
  if (has.verkoopstatus.changed && draft.verkoopstatus === Verkoopstatus.WordtVerkocht) {
    draft.verkoopstatusDatum = null;
    draft.ontbindendeVoorwaardenDatum = null;
  }

  if (draft.berekenen) {
    const verkoopkosten = typeof draft.verkoopprijs === "number" ? draft.verkoopprijs * 0.02 : null;
    if (verkoopkosten !== draft.verkoopkosten) {
      draft.verkoopkosten = verkoopkosten;
    }
  }
});

const aanTeKopenWoningDraftSideEffects = ({
  has,
  draft,
  prev
}: {
  has: any;
  draft: WoningState;
  prev: WoningState;
}): void => {
  if (draft.aanTeKopenWoning === null || prev.aanTeKopenWoning === null) {
    return;
  }

  if (has.aanTeKopenWoning.adresGelijkAanWoonadres.changed) {
    if (draft.aanTeKopenWoning.adresGelijkAanWoonadres) {
      draft.aanTeKopenWoning.adres = { ...draft.aanTeKopenWoning.woonAdres };
    }
  }

  if (
    draft.aanTeKopenWoning.adres.postcode.length > 0 &&
    draft.aanTeKopenWoning.adres.huisnummer !== null &&
    draft.aanTeKopenWoning.fotoWoning.fotoGebruikRapportage &&
    draft.aanTeKopenWoning.fotoWoning.soortFoto === SoortFoto.GoogleMaps &&
    (!reactFastCompare(draft.aanTeKopenWoning.adres, prev.aanTeKopenWoning.adres) ||
      has.aanTeKopenWoning.fotoWoning.fotoGebruikRapportage.changed ||
      // Only generate new Google Maps URL when switching to GoogleMaps if there's no existing image
      // or if the existing image is already a Google Maps URL and no stored street view URL exists
      (has.aanTeKopenWoning.fotoWoning.soortFoto.changed &&
        (!draft.aanTeKopenWoning.fotoWoning.locatieFoto ||
          ((draft.aanTeKopenWoning.fotoWoning.locatieFoto.includes("googleapis.com") ||
            draft.aanTeKopenWoning.fotoWoning.locatieFoto.includes("maps.google")) &&
            !draft.aanTeKopenWoning.fotoWoning.streetViewUrl))))
  ) {
    // Use stored Street View URL if available, otherwise generate new from address
    if (draft.aanTeKopenWoning.fotoWoning.streetViewUrl) {
      draft.aanTeKopenWoning.fotoWoning.locatieFoto = draft.aanTeKopenWoning.fotoWoning.streetViewUrl;
    } else {
      const locationStr = GetAdresEncoded(draft.aanTeKopenWoning.adres);
      const mapsUrl = GetMapsImageUrl(locationStr);
      draft.aanTeKopenWoning.fotoWoning.locatieFoto = mapsUrl;
    }
  }
  // indien foto voor rapportage gebruiken uit gezet wordt; locatie foto leegmaken.
  if (has.aanTeKopenWoning.fotoWoning.fotoGebruikRapportage.changed) {
    draft.aanTeKopenWoning.fotoWoning.locatieFoto = draft.aanTeKopenWoning.fotoWoning.fotoGebruikRapportage
      ? draft.aanTeKopenWoning.fotoWoning.locatieFoto
      : "";
    draft.aanTeKopenWoning.fotoWoning.soortFoto = draft.aanTeKopenWoning.fotoWoning.fotoGebruikRapportage
      ? draft.aanTeKopenWoning.fotoWoning.soortFoto === SoortFoto.Geen
        ? SoortFoto.Eigen
        : draft.aanTeKopenWoning.fotoWoning.soortFoto
      : SoortFoto.Geen;
  }

  // opschonen 'constructieOmschrijving' indien constructie anders dan '99'
  draft.teVerkopenWoningen.forEach((woning, index) => {
    const woningChanged = has.teVerkopenWoningen[index];
    if (!woningChanged.changed && !woningChanged.constructie.changed && !woningChanged.verkoopondervoorwaarden.changed)
      return;

    if (!woning.verkoopondervoorwaarden) {
      woning.constructie = null;
      woning.constructieOmschrijving = null;
    }

    if (
      woning.constructie == null ||
      (woning.constructie !== `${HdnAnders}` && hasValue(woning.constructieOmschrijving))
    ) {
      woning.constructieOmschrijving = null;
    }
  });
};

const bestaandeWoningDraftSideEffects = ({
  has,
  draft,
  prev
}: {
  has: any;
  draft: WoningState;
  prev: WoningState;
}): void => {
  if (draft.huidigeWoningen === null || prev.huidigeWoningen === null) {
    return;
  }

  const huidigeWoningIndex = draft.huidigeWoningen.findIndex(woning => woning.pandId === draft.hypotheekOpWoning);
  if (huidigeWoningIndex < 0) {
    return;
  }

  const huidigeWoning = draft.huidigeWoningen[huidigeWoningIndex];
  const prevHuidigeWoning = prev.huidigeWoningen.find(woning => woning.pandId === draft.hypotheekOpWoning);

  // Manually check if fotoWoning properties changed since has.huidigeWoning path doesn't exist
  const fotoGebruikRapportageChanged =
    !prevHuidigeWoning ||
    prevHuidigeWoning.fotoWoning?.fotoGebruikRapportage !== huidigeWoning.fotoWoning?.fotoGebruikRapportage;
  const soortFotoChanged =
    !prevHuidigeWoning || prevHuidigeWoning.fotoWoning?.soortFoto !== huidigeWoning.fotoWoning?.soortFoto;

  if (
    huidigeWoning.adres.postcode.length > 0 &&
    huidigeWoning.adres.huisnummer !== null &&
    huidigeWoning.fotoWoning.fotoGebruikRapportage &&
    huidigeWoning.fotoWoning.soortFoto === SoortFoto.GoogleMaps &&
    (fotoGebruikRapportageChanged || soortFotoChanged)
  ) {
    // Use stored Street View URL if available, otherwise generate new from address
    if (huidigeWoning.fotoWoning.streetViewUrl) {
      huidigeWoning.fotoWoning.locatieFoto = huidigeWoning.fotoWoning.streetViewUrl;
    } else {
      const locationStr = GetAdresEncoded(huidigeWoning.adres);
      const mapsUrl = GetMapsImageUrl(locationStr);
      huidigeWoning.fotoWoning.locatieFoto = mapsUrl;
    }
  }
  // indien foto voor rapportage gebruiken uit gezet wordt; locatie foto leegmaken.
  if (fotoGebruikRapportageChanged) {
    huidigeWoning.fotoWoning.locatieFoto = huidigeWoning.fotoWoning.fotoGebruikRapportage
      ? huidigeWoning.fotoWoning.locatieFoto
      : "";
    huidigeWoning.fotoWoning.soortFoto = huidigeWoning.fotoWoning.fotoGebruikRapportage
      ? huidigeWoning.fotoWoning.soortFoto === SoortFoto.Geen
        ? SoortFoto.Eigen
        : huidigeWoning.fotoWoning.soortFoto
      : SoortFoto.Geen;
  }
};

export const woningDraftSideEffects = createISWSideEffect<WoningState>(({ has, draft, subset, prev }): void => {
  aanTeKopenWoningDraftSideEffects({ has, draft, prev });

  draft.teVerkopenWoningen.forEach((_, index): void => {
    teVerkopenWoningenDraftSideEffects(subset.teVerkopenWoningen[index].create());
  });

  bestaandeWoningDraftSideEffects({ has, draft, prev });
});

const aanTeKopenWoningDraftSideEffectsV1 = ({
  has,
  draft,
  prev
}: {
  has: any;
  draft: WoningState;
  prev: WoningState;
}): void => {
  if (draft.aanTeKopenWoning === null || prev.aanTeKopenWoning === null) {
    return;
  }

  if (has.aanTeKopenWoning.adresGelijkAanWoonadres.changed) {
    if (draft.aanTeKopenWoning.adresGelijkAanWoonadres) {
      draft.aanTeKopenWoning.adres = { ...draft.aanTeKopenWoning.woonAdres };
    }
  }

  if (
    draft.aanTeKopenWoning.adres.postcode.length > 0 &&
    draft.aanTeKopenWoning.adres.huisnummer !== null &&
    draft.aanTeKopenWoning.fotoWoning.fotoGebruikRapportage &&
    draft.aanTeKopenWoning.fotoWoning.soortFoto === SoortFoto.GoogleMaps &&
    (!reactFastCompare(draft.aanTeKopenWoning.adres, prev.aanTeKopenWoning.adres) ||
      has.aanTeKopenWoning.fotoWoning.fotoGebruikRapportage.changed ||
      has.aanTeKopenWoning.fotoWoning.soortFoto.changed)
  ) {
    const locationStr = GetAdresEncoded(draft.aanTeKopenWoning.adres);
    const mapsUrl = GetMapsImageUrl(locationStr)
      .replace("https:", "")
      .replace("//", "https://");

    draft.aanTeKopenWoning.fotoWoning.locatieFoto = mapsUrl;
  }
  // indien foto voor rapportage gebruiken uit gezet wordt; locatie foto leegmaken.
  if (has.aanTeKopenWoning.fotoWoning.fotoGebruikRapportage.changed) {
    draft.aanTeKopenWoning.fotoWoning.locatieFoto = draft.aanTeKopenWoning.fotoWoning.fotoGebruikRapportage
      ? draft.aanTeKopenWoning.fotoWoning.locatieFoto
      : "";
    draft.aanTeKopenWoning.fotoWoning.soortFoto = draft.aanTeKopenWoning.fotoWoning.fotoGebruikRapportage
      ? draft.aanTeKopenWoning.fotoWoning.soortFoto
      : SoortFoto.Geen;
  }

  // opschonen 'constructieOmschrijving' indien constructie anders dan '99'
  draft.teVerkopenWoningen.forEach((woning, index) => {
    const woningChanged = has.teVerkopenWoningen[index];
    if (!woningChanged.changed && !woningChanged.constructie.changed && !woningChanged.verkoopondervoorwaarden.changed)
      return;

    if (!woning.verkoopondervoorwaarden) {
      woning.constructie = null;
      woning.constructieOmschrijving = null;
    }

    if (
      woning.constructie == null ||
      (woning.constructie !== `${HdnAnders}` && hasValue(woning.constructieOmschrijving))
    ) {
      woning.constructieOmschrijving = null;
    }
  });
};

const bestaandeWoningDraftSideEffectsV1 = ({
  has,
  draft,
  prev
}: {
  has: any;
  draft: WoningState;
  prev: WoningState;
}): void => {
  if (draft.huidigeWoningen === null || prev.huidigeWoningen === null) {
    return;
  }

  const huidigeWoningIndex = draft.huidigeWoningen.findIndex(woning => woning.pandId === draft.hypotheekOpWoning);
  if (huidigeWoningIndex < 0) {
    return;
  }

  const huidigeWoning = draft.huidigeWoningen[huidigeWoningIndex];
  const prevHuidigeWoning = prev.huidigeWoningen.find(woning => woning.pandId === draft.hypotheekOpWoning);

  // Manually check if fotoWoning properties changed since has.huidigeWoning path doesn't exist
  const fotoGebruikRapportageChanged =
    !prevHuidigeWoning ||
    prevHuidigeWoning.fotoWoning?.fotoGebruikRapportage !== huidigeWoning.fotoWoning?.fotoGebruikRapportage;
  const soortFotoChanged =
    !prevHuidigeWoning || prevHuidigeWoning.fotoWoning?.soortFoto !== huidigeWoning.fotoWoning?.soortFoto;

  if (
    huidigeWoning.adres.postcode.length > 0 &&
    huidigeWoning.adres.huisnummer !== null &&
    huidigeWoning.fotoWoning.fotoGebruikRapportage &&
    huidigeWoning.fotoWoning.soortFoto === SoortFoto.GoogleMaps &&
    (fotoGebruikRapportageChanged || soortFotoChanged)
  ) {
    const locationStr = GetAdresEncoded(huidigeWoning.adres);
    const mapsUrl = GetMapsImageUrl(locationStr)
      .replace("https:", "")
      .replace("//", "https://");

    huidigeWoning.fotoWoning.locatieFoto = mapsUrl;
  }
  // indien foto voor rapportage gebruiken uit gezet wordt; locatie foto leegmaken.
  if (fotoGebruikRapportageChanged) {
    huidigeWoning.fotoWoning.locatieFoto = huidigeWoning.fotoWoning.fotoGebruikRapportage
      ? huidigeWoning.fotoWoning.locatieFoto
      : "";
    huidigeWoning.fotoWoning.soortFoto = huidigeWoning.fotoWoning.fotoGebruikRapportage
      ? huidigeWoning.fotoWoning.soortFoto
      : SoortFoto.Geen;
  }
};

export const woningDraftSideEffectsV1 = createISWSideEffect<WoningState>(({ has, draft, subset, prev }): void => {
  aanTeKopenWoningDraftSideEffectsV1({ has, draft, prev });

  draft.teVerkopenWoningen.forEach((_, index): void => {
    teVerkopenWoningenDraftSideEffects(subset.teVerkopenWoningen[index].create());
  });

  bestaandeWoningDraftSideEffectsV1({ has, draft, prev });
});

/* eslint-disable-next-line @typescript-eslint/explicit-function-return-type */
export const determineWoningSideEffects = (v2: boolean) =>
  initISWSideEffect(v2 ? woningDraftSideEffects : woningDraftSideEffectsV1)();
